import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as RefreshIcon } from 'assets/icons/refresh.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

/**
 * Banner shown at the top of the application when a new build/deployment version is available.
 * Dynamically updates the CSS variable --version-bar-height so that layout elements (Sidebar, Content)
 * offset properly without creating page scrollbars or cutoffs.
 */
const VersionNoticeBar = ({
  isReloading = false,
  onReload,
  onDismiss,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateHeight = () => {
      const height = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--version-bar-height', `${height}px`);
    };

    updateHeight();

    let resizeObserver;
    if (typeof window !== 'undefined' && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(el);
    } else {
      window.addEventListener('resize', updateHeight);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateHeight);
      }
      document.documentElement.style.setProperty('--version-bar-height', '0px');
    };
  }, []);

  return (
    <NoticeWrapper ref={containerRef} role="status" aria-live="polite">
      <div className="notice-content">
        <i className="notice-icon"><InfoIcon /></i>
        <div className="notice-text">
          <strong>A new version of Percentology is available</strong>
          <span>A new application update was published. Reload the page to load the latest features and improvements.</span>
        </div>
      </div>
      <div className="notice-actions">
        <button
          type="button"
          className="reload-btn"
          onClick={onReload}
          disabled={isReloading}
        >
          <i className={isReloading ? 'refresh-icon-spin' : ''}><RefreshIcon /></i>
          {isReloading ? 'Reloading...' : 'Reload App'}
        </button>
      </div>
      {onDismiss && (
        <button
          type="button"
          className="dismiss-btn"
          onClick={onDismiss}
          aria-label="Dismiss notice"
          disabled={isReloading}
        >
          <CloseIcon />
        </button>
      )}
    </NoticeWrapper>
  );
};

const NoticeWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 10px 48px 10px 20px;
  background-color: ${(props) => props?.theme?.colors?.alertLight || '#E0F4FF'};
  border-bottom: 1px solid #B8E3FF;
  color: ${(props) => props?.theme?.colors?.blue || '#30455B'};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1440px) {
    padding: 8px 42px 8px 15px;
  }

  .notice-content {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex: 1 1 auto;
    min-width: 0;
  }

  .notice-icon {
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    margin-top: 2px;
    display: inline-block;
    svg {
      width: 100%;
      height: 100%;
    }
  }

  .notice-text {
    display: flex;
    flex-direction: column;
    strong {
      font-weight: 600;
      font-size: 13px;
      line-height: 18px;
    }
    span {
      font-weight: 400;
      font-size: 12px;
      line-height: 17px;
      color: ${(props) => props?.theme?.colors?.darkGray || '#4D4D4D'};
    }
  }

  .notice-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  .reload-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    border: none;
    border-radius: 32px;
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    color: ${(props) => props?.theme?.colors?.white || '#ffffff'};
    background-color: ${(props) => props?.theme?.colors?.primary || '#4F6D8D'};
    transition: all 0.25s ease-in-out;
    cursor: pointer;

    &:hover:not(:disabled) {
      background-color: ${(props) => props?.theme?.colors?.blue || '#30455B'};
    }
    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    i {
      width: 14px;
      height: 14px;
      display: inline-flex;
      svg {
        width: 100%;
        height: 100%;
      }
    }
  }

  .dismiss-btn {
    position: absolute;
    top: 10px;
    right: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: ${(props) => props?.theme?.colors?.darkGray || '#555555'};
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;

    svg {
      width: 12px;
      height: 12px;
    }

    &:hover:not(:disabled) {
      background-color: rgba(0, 0, 0, 0.07);
      color: ${(props) => props?.theme?.colors?.black || '#000000'};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .refresh-icon-spin {
    animation: notice-spin 1s linear infinite;
  }

  @keyframes notice-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export default VersionNoticeBar;


